import {
  FluidColor,
  FluidComponentDefinition,
  FluidIconKey,
  FluidSize,
  FluidSortOrder,
} from '@lmig/fluid-core';
import { toInlineEvent } from '../tooling/component-prop.utils';
import { h } from '@stencil/core';
import { FluidI18nStore } from '../providers/fluid-i18n-provider/store/fluid-i18n.store';

/**
 * Use in place of a text node to conditionally render a span element with translated
 * text, or fallback to <slot/> if no translation available.
 * @param props
 * @constructor
 */
export const TranslatableSlot = (props: {
  store?: FluidI18nStore;
  translationKey?: string;
}) => {
  const { store, translationKey } = props;
  if (!translationKey) return <slot />;
  const translated: string = store?.translate(translationKey);
  return translated ? <span innerHTML={translated} /> : <slot />;
};

export type FluidEventHandlerMap = Record<string, FluidCustomEventHandler>;
export type FluidCustomEventHandler = (...args: any) => any;
/**
 * Renders any given FLUID component from a FluidComponentDefinition - this method is cleaner than using
 * the (now deprecated) fluid-component-renderer, as it uses native TSX utilities to apply props and events,
 * whilst also not nesting the rendered component inside a further shadow root.
 *
 * @param props
 * @constructor
 */
export const FluidComponentFromDefinition = (props: {
  definition: FluidComponentDefinition;
  setRef?: (elRef: any) => void;
  skipEvents?: boolean;
}) => {
  const { definition, setRef, skipEvents } = props;
  const Component = definition.component;
  const events: FluidEventHandlerMap = skipEvents
    ? {}
    : convertToFluidEventMap(definition, true);

  return (
    <Component
      ref={(el) => !!setRef && setRef(el)}
      innerHTML={getNamedSlotContent(definition)}
      {...definition.props}
      {...events}>
      {definition.children?.map((child) => (
        <FluidComponentFromDefinition definition={child} setRef={null} />
      ))}
      {renderContent(definition)}
    </Component>
  );
};

// ================================================================ //
// -- Utilities (Private)

export function convertToFluidEventMap(
  definition: FluidComponentDefinition,
  inlineEvents?: boolean
): FluidEventHandlerMap {
  if (!definition.events) return {};

  const eventMapEntry = (
    eventKey: string,
    handler: FluidCustomEventHandler
  ): FluidEventHandlerMap => {
    const key = inlineEvents ? toInlineEvent(eventKey) : eventKey;
    return {
      [key]: (eventData: CustomEvent) =>
        typeof handler === 'function' ? handler(eventData) : null,
    };
  };

  return Array.isArray(definition.events)
    ? definition.events.reduce(
        (eventMap: FluidEventHandlerMap, { eventKey, handler }) => ({
          ...eventMap,
          ...eventMapEntry(eventKey, handler),
        }),
        {}
      )
    : Object.entries(definition.events).reduce(
        (eventMap: FluidEventHandlerMap, [eventKey, handler]) => ({
          ...eventMap,
          ...eventMapEntry(eventKey, handler as FluidCustomEventHandler),
        }),
        {}
      );
}

/**
 * Returns true if FluidComponentDefinition "content" contains "slot"
 * attribute (i.e. is child of named slot), false if not.
 * @param content - "content" property from FluidComponentDefinition object
 */
const hasNamedSlot = (content: any): boolean =>
  String(content).includes('slot=');

/**
 * Returns FluidComponentDefinition.content that targets a named slot
 * so that it can be directly injected into the component's body as HTML.
 * @param definition - FluidComponentDefinition to extract named slot child content from
 */
const getNamedSlotContent = (definition: FluidComponentDefinition) => {
  if (!hasNamedSlot(definition.content)) return;
  return definition.content;
};

/**
 * Returns <span> with FluidComponentDefinition.content injected as HTML.
 * Content must NOT have child elements that target a named slot within
 * the component.
 * @param definition - FluidComponentDefinition to extract content from
 */
const renderContent = (definition: FluidComponentDefinition): HTMLElement => {
  if (!definition.content || hasNamedSlot(definition.content)) return;
  return <span innerHTML={definition.content} />;
};

/**
 * Returns a FluidComponent if renderComponent is given
 * Otherwise simply returns the value that is given
 * Optionally a sort icon is returned as well if the sorted prop is TRUE
 * @param props
 */
export const RenderValue = (props: {
  value: any;
  externalState?: Record<string, unknown>;
  renderComponent?: (
    value,
    externalState?: Record<string, unknown>
  ) => FluidComponentDefinition;
  sorted?: boolean;
  sortDirection?: FluidSortOrder;
}) => {
  const sortIcon = props.sorted && (
    <div class={'group-sort-icon'}>
      <fluid-icon
        config={{
          size: FluidSize.XX_SMALL,
          color: FluidColor.LIGHT_GRAY,
          key:
            props.sortDirection === FluidSortOrder.ASC
              ? FluidIconKey.SORT_UP
              : FluidIconKey.SORT_DOWN,
        }}
      />
    </div>
  );
  return props.renderComponent
    ? [
        <FluidComponentFromDefinition
          definition={props.renderComponent(props.value, props.externalState)}
        />,
        sortIcon,
      ]
    : [props.value, sortIcon];
};
